import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { LanguageToggle } from "@/components/LanguageToggle"
import { ArrowLeft, Sparkles, Loader2, ShoppingCart, Monitor, Heart, DollarSign, GraduationCap } from "lucide-react"
import { api } from "@/services/api"
import { LeadCaptureModal } from "@/components/LeadCaptureModal"
import type { DemoTemplate, LeadFormData } from "@/types"

// Fallback templates in case API fails
const FALLBACK_TEMPLATES: DemoTemplate[] = [
  {
    id: "ecommerce",
    name: "E-commerce",
    description: "Product support, orders, shipping, and returns",
    industry: "Retail",
    icon: "shopping-cart",
    documents: [],
    sample_questions: [
      "What's your return policy?",
      "How do I track my order?",
      "Do you ship internationally?"
    ],
    is_active: true,
    usage_count: 1250
  },
  {
    id: "saas",
    name: "SaaS Support",
    description: "Feature guides, troubleshooting, and billing",
    industry: "Technology",
    icon: "monitor",
    documents: [],
    sample_questions: [
      "How do I reset my password?",
      "What features are in the Pro plan?",
      "How do I integrate with API?"
    ],
    is_active: true,
    usage_count: 980
  },
  {
    id: "healthcare",
    name: "Healthcare",
    description: "Appointment booking, insurance, and general info",
    industry: "Healthcare",
    icon: "heart",
    documents: [],
    sample_questions: [
      "How do I schedule an appointment?",
      "What insurance do you accept?",
      "What are your office hours?"
    ],
    is_active: true,
    usage_count: 750
  },
  {
    id: "financial",
    name: "Financial Services",
    description: "Account info, loan products, and inquiries",
    industry: "Finance",
    icon: "dollar-sign",
    documents: [],
    sample_questions: [
      "What are your loan rates?",
      "How do I open an account?",
      "What documents do I need?"
    ],
    is_active: true,
    usage_count: 620
  },
  {
    id: "education",
    name: "Education",
    description: "Course info, admissions, and campus services",
    industry: "Education",
    icon: "graduation-cap",
    documents: [],
    sample_questions: [
      "What courses do you offer?",
      "How do I apply?",
      "What are the tuition fees?"
    ],
    is_active: true,
    usage_count: 540
  }
]

const iconMap: Record<string, React.ReactNode> = {
  "shopping-cart": <ShoppingCart className="w-8 h-8" />,
  "monitor": <Monitor className="w-8 h-8" />,
  "heart": <Heart className="w-8 h-8" />,
  "dollar-sign": <DollarSign className="w-8 h-8" />,
  "graduation-cap": <GraduationCap className="w-8 h-8" />,
}

const colorMap: Record<string, string> = {
  "ecommerce": "from-orange-500 to-red-500",
  "saas": "from-blue-500 to-indigo-500",
  "healthcare": "from-rose-500 to-pink-500",
  "financial": "from-emerald-500 to-teal-500",
  "education": "from-violet-500 to-purple-500",
}

export function TemplateGalleryPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<DemoTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRateLimited, setIsRateLimited] = useState(false)
  const [showLeadModal, setShowLeadModal] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    setIsLoading(true)
    setError(null)

    const result = await api.getTemplates()

    if (result.success && result.data && result.data.length > 0) {
      setTemplates(result.data)
    } else {
      // Use fallback templates
      setTemplates(FALLBACK_TEMPLATES)
    }

    setIsLoading(false)
  }

  const handleSelectTemplate = async (templateId: string) => {
    setSelectedTemplate(templateId)

    // Start a new session with this template
    const result = await api.startDemo("template")

    if (result.success && result.data) {
      const sessionId = result.data.id

      // Select the template
      const selectResult = await api.selectTemplate(sessionId, templateId)

      if (selectResult.success) {
        navigate("/demo/chat", {
          state: {
            sessionId,
            mode: "template",
            templateId
          }
        })
      } else {
        setError(selectResult.error || "Failed to select template")
        setSelectedTemplate(null)
      }
    } else {
      if (result.status === 429) {
        setIsRateLimited(true)
      } else {
        setError(result.error || "Failed to start demo session")
      }
      setSelectedTemplate(null)
    }
  }

  const handleLeadSubmit = async (data: LeadFormData) => {
    await api.captureLead(null, data)
    setShowLeadModal(false)
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/demo/select")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("templateGallery.back")}
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-lg hidden sm:block">{t("templateGallery.headerTitle")}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <LanguageToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("templateGallery.title")}
            </h1>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("templateGallery.subtitle")}
            </p>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
              <p className="text-muted">{t("templateGallery.loading")}</p>
            </div>
          )}

          {/* Rate Limit State */}
          {isRateLimited && !isLoading && (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <h2 className="text-2xl font-bold text-foreground mb-3">{t("rateLimit.title")}</h2>
                <p className="text-muted mb-8">{t("rateLimit.message")}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => setShowLeadModal(true)}>
                    {t("rateLimit.contactUs")}
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    {t("rateLimit.tryLater")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && !isRateLimited && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadTemplates} variant="outline">
                {t("templateGallery.tryAgain")}
              </Button>
            </div>
          )}

          {/* Templates Grid */}
          {!isLoading && !error && !isRateLimited && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className="group cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  onClick={() => handleSelectTemplate(template.id)}
                >
                  <CardContent className="p-0">
                    {/* Header with gradient */}
                    <div className={`h-32 bg-gradient-to-br ${colorMap[template.id] || "from-primary to-secondary"} flex items-center justify-center`}>
                      <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                        {iconMap[template.icon] || <Sparkles className="w-8 h-8" />}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-foreground">
                          {t(`landing.industries.${template.id}`, { defaultValue: template.name })}
                        </h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary/10 text-secondary font-medium">
                          {t(`landing.industries.${template.id}`, { defaultValue: template.industry })}
                        </span>
                      </div>

                      <p className="text-muted mb-4">
                        {t(`landing.industries.${template.id}Desc`, { defaultValue: template.description })}
                      </p>

                      {/* Sample Questions */}
                      <div className="mb-6">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                          {t("templateGallery.tryAsking")}
                        </p>
                        <ul className="space-y-1">
                          {["q1", "q2"].map((qKey) => (
                            <li key={qKey} className="text-sm text-foreground flex items-start">
                              <span className="text-primary mr-2">•</span>
                              <span className="line-clamp-1">
                                {t(`suggestedQuestions.${template.id}.${qKey}`, { defaultValue: "" })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Action Button */}
                      <Button
                        className="w-full"
                        disabled={selectedTemplate === template.id}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSelectTemplate(template.id)
                        }}
                      >
                        {selectedTemplate === template.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t("templateGallery.loading")}
                          </>
                        ) : (
                          t("templateGallery.tryDemo")
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Tip */}
          <div className="mt-12 p-6 rounded-xl bg-primary/5 border border-primary/10 text-center">
            <p className="text-muted">
              <span className="font-medium text-foreground">
                {t("templateGallery.tipText")}
              </span>{" "}
              <Button
                variant="link"
                className="p-0 h-auto"
                onClick={() => navigate("/demo/upload")}
              >
                {t("templateGallery.uploadDocsLink")}
              </Button>
            </p>
          </div>
        </div>
      </main>

      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSubmit={handleLeadSubmit}
        sessionDuration={0}
        messageCount={0}
      />
    </div>
  )
}
